import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import axios from "axios";
import moment from "moment";
import * as XLSX from "xlsx";
import { useNavigate } from "react-router-dom";
import "./TripRegistration.css";
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import {
  IconButton,
  Tooltip,
  Chip,
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Alert,
  CircularProgress,
  TextField,
  MenuItem,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from "@mui/material";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import { translations } from "./translations";
const getCarrierByTruckId = (trip) => trip?.carrier || "-";

const TripRegistration = () => {
  const [trips, setTrips] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [activeTab, setActiveTab] = useState("register");
  const [loading, setLoading] = useState(true);
  const [regionLoading, setRegionLoading] = useState(false);
  const [driversLoading, setDriversLoading] = useState(false);
  const [filterMonth, setFilterMonth] = useState(moment().month() + 1);
  const [filterYear, setFilterYear] = useState(moment().year());
  const [searchTerm, setSearchTerm] = useState("");
  const [sortField, setSortField] = useState("north_trip_percentage");
  const [sortDirection, setSortDirection] = useState("asc");
  const [filterDriverCode, setFilterDriverCode] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(50);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [driverDetailsOpen, setDriverDetailsOpen] = useState(false);
  const [driverTrips, setDriverTrips] = useState([]);
  const [driverTripsLoading, setDriverTripsLoading] = useState(false);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [driverSearchTerm, setDriverSearchTerm] = useState("");
  const [offloadingDateLabels, setOffloadingDateLabels] = useState([]);
  const [selectedDriverTemp, setSelectedDriverTemp] = useState(null);
  const [dropdownPosition, setDropdownPosition] = useState("down");
  const [allocationDate, setAllocationDate] = useState(
    moment().add(1, "day").format("YYYY-MM-DD")
  );
  const [allocationRows, setAllocationRows] = useState([]);
  const [allocationLoading, setAllocationLoading] = useState(false);
  const [allocationSearch, setAllocationSearch] = useState("");
  const [allocationMessage, setAllocationMessage] = useState(null);

  const [filteredCount, setFilteredCount] = useState(0);
  const gridRef = useRef();

  const updateFilteredCount = () => {
    if (gridRef.current && gridRef.current.api) {
      setFilteredCount(gridRef.current.api.getDisplayedRowCount());
    }
  };

  const navigate = useNavigate();
  const userRole = String(localStorage.getItem("userrole") || "").trim().toLowerCase();
  const drivercivilid = localStorage.getItem("badge");
  const isDriver = ["driver", "driver_supv"].includes(userRole);

  const fetchActiveDrivers = async () => {
    setDriversLoading(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/drivers-list`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const activeDrivers = (response.data.data || [])
        .filter((d) => d.is_active === "Y")
        .map((d) => ({
          ...d,
          driver_name: d.driver_name,
          reporting_driver_code: d.reporting_driver_code || "",
        }));
      setDrivers(activeDrivers);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setDrivers([]);
    }
    setDriversLoading(false);
  };

  // List of priority trucks (normalized: no spaces or slashes, uppercase)
  const PRIORITY_TRUCKS = [
    '7103HA','1553YS','4985YR','6502MA','7135RA','371YM','7745MR','1804BA',
    '2557YM','5622BK','624RA','3847MK','4556MK','6678MA','4220ML','3526MB',
    '5142MB','1550MK','3167MD','1477MD','144RA','9299YW','50YM','899HM','2227WK'
  ];

  const normalizeTruckId = (id) => {
    if (!id && id !== 0) return "";
    return String(id).toUpperCase().replace(/\s+/g, '').replace(/\//g, '');
  };

  const isPriorityTruck = (truckId) => {
    const n = normalizeTruckId(truckId);
    return PRIORITY_TRUCKS.includes(n);
  };

  // Normalize field names from backend (uppercase) to frontend (lowercase)
  const normalizeResponseData = (data) => {
    if (Array.isArray(data)) {
      return data.map(item => {
        const normalized = {};
        Object.keys(item).forEach(key => {
          const lowerKey = key.toLowerCase();
          normalized[lowerKey] = item[key];
        });
        return normalized;
      });
    }
    return data;
  };

  const fetchRegionData = useCallback(
    async (
      month = filterMonth,
      year = filterYear,
      reporting_driver_code = filterDriverCode
    ) => {
      if (isDriver) return;

      setRegionLoading(true);
      try {
        const token = localStorage.getItem("token");
        const params = new URLSearchParams();
        if (month) params.append("month", month);
        if (year) params.append("year", year);
        if (reporting_driver_code)
          params.append("reporting_driver_code", reporting_driver_code);

        const url = `${
          process.env.REACT_APP_BASE_API_URL
        }/north-trips-percentage?${params.toString()}`;
        const response = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setRegionData(response.data || []);
        setCurrentPage(1);
      } catch (error) {
        console.error(
          "Error fetching region data:",
          error?.response?.data || error.message
        );
        setRegionData([]);
      }
      setRegionLoading(false);
    },
    [filterMonth, filterYear, filterDriverCode, isDriver]
  );

  const fetchDriverTripsDetails = async (driverCode) => {
    setDriverTripsLoading(true);
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams();
      params.append("month", filterMonth);
      params.append("year", filterYear);

      const response = await axios.get(
        `${
          process.env.REACT_APP_BASE_API_URL
        }/driver-trips-details/${driverCode}?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDriverTrips(response.data || []);
    } catch (error) {
      console.error("Error fetching driver trip details:", error);
      setDriverTrips([]);
    }
    setDriverTripsLoading(false);
  };

  const handleDriverClick = async (driver) => {
    setSelectedDriver(driver);
    setDriverDetailsOpen(true);
    if (driver.reporting_driver_code) {
      await fetchDriverTripsDetails(driver.reporting_driver_code);
    }
  };

  const handleCloseDriverDetails = () => {
    setDriverDetailsOpen(false);
    setSelectedDriver(null);
    setDriverTrips([]);
  };

  const handleDriverSelection = (driver) => {
    if (driver?.reporting_driver_code) {
      setFilterDriverCode(driver.reporting_driver_code);
      fetchRegionData(filterMonth, filterYear, driver.reporting_driver_code);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const userRole = String(localStorage.getItem("userrole") || "").trim().toLowerCase();
        const driverBadge = localStorage.getItem("badge");
        
        // For driver role, only fetch their own trips using badge
        const url =
          userRole === "driver" && driverBadge
            ? `${process.env.REACT_APP_BASE_API_URL}/trip-registrationscode/driver/${driverBadge}`
            : `${process.env.REACT_APP_BASE_API_URL}/trip-registrations`;

        const tripsRes = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const responseData = tripsRes.data || [];
        if (Array.isArray(responseData)) {
          setTrips(normalizeResponseData(responseData));
          setOffloadingDateLabels([]);
        } else {
          const normalizedTrips = normalizeResponseData(responseData.trips || []);
          setTrips(normalizedTrips);
          setOffloadingDateLabels(responseData.offloadingDateLabels || []);
        }
      } catch (err) {
        console.error("Error fetching trips:", err);
        setTrips([]);
        setOffloadingDateLabels([]);
      }
      setLoading(false);
      updateFilteredCount();
    };
    fetchData();

    if (!isDriver) {
      fetchRegionData();
      fetchActiveDrivers();
    }
  }, [fetchRegionData, isDriver]);

  useEffect(() => {
    if (activeTab === "region" && !isDriver) {
      fetchRegionData(filterMonth, filterYear, filterDriverCode);
    }
  }, [
    activeTab,
    filterMonth,
    filterYear,
    filterDriverCode,
    fetchRegionData,
    isDriver,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, perPage]);

  // Check dropdown position
  const checkDropdownPosition = (dropdownElement) => {
    if (!dropdownElement) return;

    const rect = dropdownElement.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    if (spaceBelow < 300 && spaceAbove > spaceBelow) {
      setDropdownPosition("up");
    } else {
      setDropdownPosition("down");
    }
  };

  // Filter drivers based on search term
  const filteredDrivers = useMemo(() => {
    const searchTerm = driverSearchTerm.toLowerCase().trim();
    return drivers.filter(
      (driver) =>
        driver.driver_name.toLowerCase().includes(searchTerm) ||
        (driver.reporting_driver_code &&
          driver.reporting_driver_code.toLowerCase().includes(searchTerm))
    );
  }, [drivers, driverSearchTerm]);

  const handleExport = () => {
    if (!gridRef.current || !trips.length) return;

    const filteredData = [];
    gridRef.current.api.forEachNodeAfterFilter((node) => {
      filteredData.push(node.data);
    });

    if (!filteredData.length) return;

    const exportData = filteredData.map((trip) => {
      const code = getReportingCodeFromTrip(trip);
      const stats = code ? driverStatsMap[code] : null;

      const row = {
        "Driver + ID": trip.driver_id || "-",
        "Carrier": getCarrierByTruckId(trip),
        "Truck": trip.truck_id || "-",
        "Remarks": trip.remarks || "-",
        "Registration Time": trip.registration_time
          ? moment(trip.registration_time).local().format("DD MMM YYYY HH:mm")
          : "-",
        "Loading Time": trip.loading_time
          ? moment(trip.loading_time).local().format("DD MMM YYYY HH:mm")
          : "-",
        "Total Trips (Current Month)": stats ? stats.total_trips : "-",
        "North Trips (Current Month)": stats ? stats.north_trips : "-",
        "North % (Current Month)": stats
          ? `${parseFloat(stats.north_trip_percentage || 0).toFixed(1)}%`
          : "-",
      };

      const labels = offloadingDateLabels.length
        ? offloadingDateLabels
        : [
            "Offloading Date 1",
            "Offloading Date 2",
            "Offloading Date 3",
            "Offloading Date 4",
            "Offloading Date 5",
          ];

      labels.forEach((label, index) => {
        row[label] = trip[`offloading_point_${index + 1}`] || "-";
      });

      return row;
    });

    const ws = XLSX.utils.json_to_sheet(exportData);
    // compute column widths based on max string length per column
    if (exportData.length) {
      const headers = Object.keys(exportData[0]);
      const cols = headers.map((h) => {
        let maxLen = h.length; 
        for (let i = 0; i < exportData.length; i++) {
          const val = exportData[i][h];
          const len = val ? String(val).length : 0;
          if (len > maxLen) maxLen = len;
        }
        // add padding and cap width
        const wch = Math.min(Math.max(maxLen + 2, 10), 60);
        return { wch };
      });
      ws["!cols"] = cols;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Trips");
    XLSX.writeFile(
      wb,
      `Trip_Registrations_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`
    );
  };

  // Update handleExportRegionData similarly to set widths
  const handleExportRegionData = () => {
    if (!filteredRegionData || !filteredRegionData.length) return;

    const exportData = filteredRegionData.map((item, index) => ({
      No: index + 1,
      "Driver Code": item.reporting_driver_code,
      "Driver Name": item.driver_name,
      "South Trips": item.south_trips,
      "North Trips": item.north_trips,
      "North Trip Percentage": item.north_trip_percentage,
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);

    if (exportData.length) {
      const headers = Object.keys(exportData[0]);
      const cols = headers.map((h) => {
        let maxLen = h.length;
        for (let i = 0; i < exportData.length; i++) {
          const val = exportData[i][h];
          const len = val !== null && val !== undefined ? String(val).length : 0;
          if (len > maxLen) maxLen = len;
        }
        const wch = Math.min(Math.max(maxLen + 2, 8), 60);
        return { wch };
      });
      ws["!cols"] = cols;
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Region Distribution");
    const fileName = `Region_Distribution_${filterMonth}_${filterYear}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  // Filter and sort region data
  const filteredRegionDataRaw = useMemo(() => {
    let filtered = (regionData || []).filter(
      (item) =>
        item.driver_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.reporting_driver_code
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        item.carrier?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Sort data
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      if (sortField === "north_trip_percentage") {
        aValue = parseFloat(aValue || 0);
        bValue = parseFloat(bValue || 0);
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [regionData, searchTerm, sortField, sortDirection]);

  // Normalize numeric fields
  const filteredRegionData = useMemo(() => {
    return (filteredRegionDataRaw || []).map((r) => ({
      ...r,
      total_trips: Number(r.total_trips || 0),
      north_trips: Number(r.north_trips || 0),
      south_trips: Number(r.south_trips || 0),
      suggested_region: r.suggested_region || "SOUTH",
      north_trip_percentage: parseFloat(r.north_trip_percentage || 0),
      allocation_priority: r.allocation_priority || "Unknown",
    }));
  }, [filteredRegionDataRaw]);

  // Compute region statistics
  const regionStats = useMemo(() => {
    if (!filteredRegionData.length) return null;

    const totalDrivers = filteredRegionData.length;
    const totalTrips = filteredRegionData.reduce(
      (sum, item) => sum + (item.total_trips || 0),
      0
    );
    const totalNorthTrips = filteredRegionData.reduce(
      (sum, item) => sum + (item.north_trips || 0),
      0
    );
    const avgPercentage =
      filteredRegionData.reduce(
        (sum, item) => sum + (Number(item.north_trip_percentage) || 0),
        0
      ) / totalDrivers;

    const priorityCounts = filteredRegionData.reduce(
      (acc, item) => {
        acc[item.allocation_priority] =
          (acc[item.allocation_priority] || 0) + 1;
        return acc;
      },
      {
        /*asas*/
      }
    );

    return {
      totalDrivers,
      totalTrips,
      totalNorthTrips,
      avgPercentage: avgPercentage.toFixed(1),
      priorityCounts,
    };
  }, [filteredRegionData]);

  // Paged data for rendering
  const pagedRegionData = useMemo(() => {
    const start = (currentPage - 1) * perPage;
    return filteredRegionData.slice(start, start + perPage);
  }, [filteredRegionData, currentPage, perPage]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredRegionData.length / perPage)
  );

  const handleFilterChange = () => {
    fetchRegionData(filterMonth, filterYear, filterDriverCode);
  };

  // Handle sort
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Get sort indicator
  const getSortIndicator = (field) => {
    if (sortField !== field) return null;
    return sortDirection === "asc" ? " ↑" : " ↓";
  };

  // Handle delete trip
  const handleDelete = async (tripId) => {
    if (!window.confirm("Are you sure you want to delete this trip?")) return;

    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${process.env.REACT_APP_BASE_API_URL}/trip-registrations/${tripId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setTrips(trips.filter((trip) => trip.id !== tripId));
    } catch (error) {
      console.error("Error deleting trip:", error);
      alert("Failed to delete trip");
    }
  };

  // Handle edit trip
  const handleEdit = (trip) => {
    navigate("/trip-registration/new", { state: { editTrip: trip } });
  };

  const startNewTripWorkflow = async () => {
    // Drivers may continue with today's latest unused PASS checklist. Previously
    // this button always opened a new blank checklist, even when one existed.
    if (userRole === "driver" && drivercivilid) {
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(
          `${process.env.REACT_APP_BASE_API_URL}/driver-checklists/valid-for-trip/${encodeURIComponent(drivercivilid)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const validChecklist = response.data?.data;
        if (validChecklist?.vehicleChecklistId) {
          navigate("/trip-registration/new", {
            state: { tripWorkflow: true, ...validChecklist },
          });
          return;
        }
      } catch (error) {
        console.error("Unable to check for an existing valid checklist:", error);
      }
    }
    navigate("/driver-checklists/new", { state: { tripWorkflow: true } });
  };

  // Back button handler
  const handleBack = () => {
    navigate("/main");
  };

  // Render pagination controls
  const renderPagination = () => {
    if (!filteredRegionData.length) return null;
    return (
      <Box className="trp-pagination-container">
        <Box className="trp-pagination-controls">
          <button
            className="trp-pagination-btn"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            Prev
          </button>
          <span className="trp-pagination-info">
            Page {currentPage} of {totalPages} • Showing{" "}
            {pagedRegionData.length} of {filteredRegionData.length} drivers
          </span>
          <button
            className="trp-pagination-btn"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </Box>
        <Box className="trp-pagination-settings">
          <TextField
            size="small"
            select
            label="Show"
            value={perPage}
            onChange={(e) => {
              setPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="trp-per-page-select"
          >
            {[50, 100, 200, 300].map((n) => (
              <MenuItem key={n} value={n}>
                {n}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Box>
    );
  };

  // Render driver details modal with enhanced trip details
  const renderDriverDetails = () => {
    if (!selectedDriver) return null;

    return (
      <Dialog
        open={driverDetailsOpen}
        onClose={handleCloseDriverDetails}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle className="trp-driver-details-title">
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="h6">
              Trip Details - {selectedDriver.driver_name} (
              {selectedDriver.reporting_driver_code})
            </Typography>
            <IconButton onClick={handleCloseDriverDetails} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {/* Enhanced Driver Trips Table with Journey Details */}
          <Box className="trp-driver-trips-section">
            <Typography variant="h6" gutterBottom>
              Trip Details ({driverTrips.length} trips)
            </Typography>
            {driverTripsLoading ? (
              <Box className="trp-loading-state-modern">
                <CircularProgress size={24} />
                <Typography>Loading trip details...</Typography>
              </Box>
            ) : driverTrips.length > 0 ? (
              <TableContainer
                component={Paper}
                className="trp-table-container-modern"
              >
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Journey No</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Carrier</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Truck No</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Start Region</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Journey Date</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {driverTrips.map((trip, index) => (
                      <TableRow key={trip.JOURNEY_PLANE_NO || index}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">
                            {trip.JOURNEY_PLANE_NO || "-"}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trip.CARRIER || "-"}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trip.SCHEDULED_VEHICLE || "-"}
                            size="small"
                            variant="outlined"
                            color="primary"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={trip.START_REGION || "-"}
                            size="small"
                            color={
                              trip.start_region?.toUpperCase() === "NORTH"
                                ? "success"
                                : "default"
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={0.5}>
                            <CalendarTodayIcon
                              fontSize="small"
                              color="action"
                            />
                            <Typography variant="body2">
                              {trip.JOURNEY_PLANE_DATE
                                ? moment(trip.JOURNEY_PLANE_DATE).format(
                                    "DD MMM YYYY"
                                  )
                                : "-"}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert severity="info">
                No trip details found for this driver.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDriverDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const driverStatsMap = useMemo(() => {
    const map = {};
    (regionData || []).forEach((r) => {
      const code = (r.reporting_driver_code || "").toString();
      if (!code) return;
      map[code] = {
        total_trips: Number(r.total_trips || 0),
        north_trips: Number(r.north_trips || 0),
        south_trips: Number(r.south_trips || 0),
        suggested_region: r.suggested_region || "SOUTH",
        north_trip_percentage: parseFloat(r.north_trip_percentage || 0),
      };
    });
    return map;
  }, [regionData]);

  // helper to extract reporting code from a trip row (fallbacks)
  const getReportingCodeFromTrip = (trip = {}) => {
    if (trip.reporting_driver_code) return trip.reporting_driver_code;
    if (trip.reporting_driver_code === "") return "";
    if (trip.driver_id) {
      // looks for pattern "Name (CODE)"
      const m = String(trip.driver_id).match(/\(([^)]+)\)/);
      if (m && m[1]) return m[1];
      // fallback: maybe driver_id itself is the code
      return trip.driver_id;
    }
    return "";
  };

  const fetchAllocations = useCallback(async () => {
    if (isDriver) return;
    setAllocationLoading(true);
    setAllocationMessage(null);
    try {
      const token = localStorage.getItem("token");
      const requestedDate = moment(allocationDate, "YYYY-MM-DD", true).format("YYYY-MM-DD");
      const response = await axios.get(
        `${process.env.REACT_APP_BASE_API_URL}/trip-allocations?date=${encodeURIComponent(requestedDate)}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setAllocationRows((response.data || []).map((row) => ({ ...row, dirty: false })));
    } catch (error) {
      setAllocationRows([]);
      setAllocationMessage({ type: "error", text: error?.response?.data?.message || "Unable to load allocations." });
    } finally {
      setAllocationLoading(false);
    }
  }, [allocationDate, isDriver]);

  useEffect(() => {
    if (activeTab === "allocation" && !isDriver) fetchAllocations();
  }, [activeTab, fetchAllocations, isDriver]);

  const updateAllocation = (id, field, value) => {
    setAllocationRows((rows) => rows.map((row) =>
      row.id === id ? { ...row, [field]: value, dirty: true } : row
    ));
  };

  const cleanDriverName = (driver) => {
    if (!driver) return "No driver";
    const cleaned = String(driver).replace(/\s*\([^)]*\)/g, "").trim();
    return cleaned || driver;
  };

  const saveAllocation = async (row) => {
    if (!row.allocated_region) {
      setAllocationMessage({ type: "error", text: "Select an assigned region before saving." });
      return;
    }
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${process.env.REACT_APP_BASE_API_URL}/trip-allocations/${row.id}`, {
        allocated_region: row.allocated_region,
        suggested_region: row.suggested_region,
        override_reason: row.override_reason || null,
        allocated_by: localStorage.getItem("username") || localStorage.getItem("user") || "SYSTEM USER",
      }, { headers: { Authorization: `Bearer ${token}` } });
      setAllocationMessage({ type: "success", text: `Allocation saved for truck ${row.truck_id || "N/A"}.` });
      await fetchAllocations();
    } catch (error) {
      setAllocationMessage({ type: "error", text: error?.response?.data?.message || "Unable to save allocation." });
    }
  };

  const filteredAllocations = allocationRows.filter((row) => {
    const q = allocationSearch.toLowerCase().trim();
    return !q || [cleanDriverName(row.driver_id), row.driver_id, row.reporting_driver_code, row.truck_id, row.allocated_region]
      .some((value) => String(value || "").toLowerCase().includes(q));
  });

  const exportAllocations = () => {
    const data = filteredAllocations.map((row, index) => ({
      No: index + 1,
      "Loading Date": moment(row.loading_time).format("DD/MM/YYYY HH:mm"),
      Truck: row.truck_id || "No truck",
      Driver: cleanDriverName(row.driver_id),
      "South Trips": row.south_trips,
      "North Trips": row.north_trips,
      "North Entitlement": row.north_entitlement,
      "Reserved North": row.upcoming_north_allocations,
      "Entitlement Gap": row.entitlement_gap,
      "Suggested Region": row.suggested_region,
      "Assigned Region": row.allocated_region || "PENDING",
      "Override Reason": row.override_reason || "",
      "Assigned By": row.allocated_by || "",
      "Assigned At": row.allocated_at ? moment(row.allocated_at).format("DD/MM/YYYY HH:mm") : "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    ws["!cols"] = Object.keys(data[0] || {}).map((key) => ({ wch: Math.max(12, key.length + 3) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Load Allocation");
    XLSX.writeFile(wb, `Load_Allocation_${allocationDate}.xlsx`);
  };

  return (
    <div className="trp-reg-container">
      <div className="trp-content-wrapper">
        <div className="trp-tabs-container">
          <Box className="trp-back-button-container">
            <Tooltip title={translations["Go back"] || "Go back"}>
              <IconButton
                onClick={handleBack}
                className="trp-back-btn-modern"
                size="small"
              >
                <ArrowBackIcon />
              </IconButton>
            </Tooltip>
          </Box>

          <div className="trp-tabs-wrapper">
            <button
              className={`trp-tab-btn ${
                activeTab === "register" ? "trp-tab-active" : ""
              }`}
              onClick={() => setActiveTab("register")}
            >
              <span className="trp-tab-icon">📝</span>
              {translations["Trip Registration"] || "Trip Registration"}
            </button>

            {!isDriver && (
              <button
                className={`trp-tab-btn ${activeTab === "allocation" ? "trp-tab-active" : ""}`}
                onClick={() => setActiveTab("allocation")}
              >
                <span className="trp-tab-icon">⚖</span>
                Tomorrow Allocation
              </button>
            )}

            {!isDriver && (
              <button
                className={`trp-tab-btn ${
                  activeTab === "region" ? "trp-tab-active" : ""
                }`}
                onClick={() => setActiveTab("region")}
              >
                <span className="trp-tab-icon">📊</span>
                {translations["Region Distribution"] || "Region Distribution"}
              </button>
            )}
          </div>
        </div>

        {/* Trip Registration Tab */}
        {activeTab === "register" && (
          <div className="trp-content-section">
            <Card className="trp-card-modern" elevation={0}>
              <CardContent className="trp-card-content-modern">
                {/* Header */}
                <Box className="trp-card-header-modern">
                  <Box className="trp-card-title-group-modern">
                    <Box className="trp-header-stats-modern">
                      <Chip
                        label={
                          filteredCount !== trips.length
                            ? `${filteredCount} of ${trips.length} trips`
                            : `${trips.length} trips`
                        }
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <Box className="trp-action-buttons-modern">
                    <Tooltip title="Reset Filters">
                      <IconButton
                        size="small"
                        onClick={() => {
                          if (gridRef.current) {
                            gridRef.current.api.setFilterModel({});
                            updateFilteredCount();
                          }
                        }}
                      >
                        <FilterAltOffIcon />
                      </IconButton>
                    </Tooltip>
                    <button
                      className="trp-btn-secondary-modern trp-btn-with-icon"
                      onClick={handleExport}
                      disabled={!trips.length}
                    >
                      <DownloadIcon className="trp-btn-icon-modern" />
                      {translations["Export"] || "Export"}
                    </button>
                    <button
                      className="trp-btn-primary-modern trp-btn-with-icon"
                      onClick={startNewTripWorkflow}
                    >
                      <span className="trp-btn-icon-modern">+</span>
                      {translations["+New Trip"] || "New Trip"}
                    </button>
                  </Box>
                </Box>

                {/* Trips Table */}
                <Box className="trp-table-section-modern">
                  {loading ? (
                    <Box className="trp-loading-state-modern">
                      <CircularProgress size={40} />
                      <Typography
                        variant="h6"
                        className="trp-loading-text-modern"
                      >
                        {translations["Loading trips..."] || "Loading trips..."}
                      </Typography>
                    </Box>
                  ) : trips.length > 0 ? (
                    <div className="ag-theme-alpine" style={{ height: '680px', width: '100%' }}>
                      <AgGridReact
                        ref={gridRef}
                        rowData={trips}
                        onGridReady={(params) => {
                          params.api.sizeColumnsToFit();
                          updateFilteredCount();
                        }}
                        onFilterChanged={() => updateFilteredCount()}
                        overlayNoRowsTemplate={
                          '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;">No trips match current filters</span>'
                        }
                        columnDefs={[
                          {
                            headerName: 'Driver + ID',
                            field: 'driver_id',
                            minWidth: 350,
                            flex: 2,
                            filter: 'agTextColumnFilter',
                            cellRenderer: (params) => (
                              <Box className="trp-driver-info-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <PersonIcon className="trp-driver-icon-modern" fontSize="small" />
                                <Typography variant="body2" className="trp-driver-name-modern">
                                  {params.value || "-"}
                                </Typography>
                              </Box>
                            ),
                          },
                          {
                            headerName: 'Carrier',
                            field: 'truck_id',
                            minWidth: 120,
                            flex: 1,
                            filter: 'agTextColumnFilter',
                            valueGetter: (params) => getCarrierByTruckId(params.data),
                            cellRenderer: (params) => {
                              const carrier = params.value;
                              return (
                                <Chip
                                  label={carrier}
                                  size="small"
                                  variant="outlined"
                                  color={
                                    carrier === 'Al Madina Logistics' ? 'primary' :
                                    carrier === 'Al Mukhtar' ? 'secondary' :
                                    carrier === 'OTO' ? 'success' :
                                    carrier === 'Hamadan' ? 'warning' : 'default'
                                  }
                                  className="trp-carrier-chip-modern"
                                />
                              );
                            },
                          },
                          {
                            headerName: 'Truck',
                            field: 'truck_id',
                            minWidth: 80,
                            flex: 1,
                            filter: 'agTextColumnFilter',
                            cellRenderer: (params) => {
                              const isPriority = isPriorityTruck(params.value);
                              return (
                                <Chip
                                  label={params.value || "-"}
                                  size="small"
                                  variant="outlined"
                                  className={`trp-truck-chip-modern ${isPriority ? 'trp-priority-truck-chip' : ''}`}
                                  style={{ maxWidth: "100%" }}
                                />
                              );
                            },
                          },
                          {
                            headerName: 'Remarks',
                            field: 'remarks',
                            minWidth: 120,
                            flex: 1.5,
                            filter: 'agTextColumnFilter',
                            cellRenderer: (params) => (
                              <Typography
                                variant="body2"
                                className="trp-remarks-modern"
                                title={params.value}
                              >
                                {params.value || "-"}
                              </Typography>
                            ),
                          },
                          {
                            headerName: 'Registration Time',
                            field: 'registration_time',
                            minWidth: 130,
                            flex: 1,
                            type: 'dateColumn',
                            filter: 'agDateColumnFilter',
                            filterParams: {
                              comparator: (filterLocalDateAtMidnight, cellValue) => {
                                if (!cellValue) return -1;
                                const cellDate = new Date(cellValue);
                                const filterDate = new Date(filterLocalDateAtMidnight);
                                // Compare only date parts (year, month, day)
                                const cellYear = cellDate.getFullYear();
                                const cellMonth = cellDate.getMonth();
                                const cellDay = cellDate.getDate();
                                const filterYear = filterDate.getFullYear();
                                const filterMonth = filterDate.getMonth();
                                const filterDay = filterDate.getDate();
                                if (cellYear !== filterYear) {
                                  return cellYear - filterYear;
                                }
                                if (cellMonth !== filterMonth) {
                                  return cellMonth - filterMonth;
                                }
                                return cellDay - filterDay;
                              },
                            },
                            valueGetter: (params) =>
                              params.data.registration_time
                                ? new Date(params.data.registration_time)
                                : null,
                            cellRenderer: (params) => (
                              <Typography variant="body2" className="trp-timestamp-modern">
                                {params.data.registration_time
                                  ? moment(params.data.registration_time).format("DD MMM YYYY HH:mm")
                                  : "-"}
                              </Typography>
                            ),
                          },
                          {
                            headerName: 'Loading Time',
                            field: 'loading_time',
                            minWidth: 130,
                            flex: 1,
                            type: 'dateColumn',
                            filter: 'agDateColumnFilter',
                            filterParams: {
                              comparator: (filterLocalDateAtMidnight, cellValue) => {
                                if (!cellValue) return -1;
                                const cellDate = new Date(cellValue);
                                const filterDate = new Date(filterLocalDateAtMidnight);
                                // Compare only date parts (year, month, day)
                                const cellYear = cellDate.getFullYear();
                                const cellMonth = cellDate.getMonth();
                                const cellDay = cellDate.getDate();
                                const filterYear = filterDate.getFullYear();
                                const filterMonth = filterDate.getMonth();
                                const filterDay = filterDate.getDate();
                                if (cellYear !== filterYear) {
                                  return cellYear - filterYear;
                                }
                                if (cellMonth !== filterMonth) {
                                  return cellMonth - filterMonth;
                                }
                                return cellDay - filterDay;
                              },
                            },
                            valueGetter: (params) =>
                              params.data.loading_time
                                ? new Date(params.data.loading_time)
                                : null,
                            cellRenderer: (params) => (
                              <Typography variant="body2" className="trp-timestamp-modern">
                                {params.data.loading_time
                                  ? moment(params.data.loading_time).format("DD MMM YYYY HH:mm")
                                  : "-"}
                              </Typography>
                            ),
                          },
                          {
                            headerName: 'Total Trips (Current Month)',
                            field: 'total_trips_current',
                            width: 110,
                            minWidth: 80,
                            filter: 'agNumberColumnFilter',
                            valueGetter: (params) => {
                              const code = getReportingCodeFromTrip(params.data);
                              const stats = code ? driverStatsMap[code] : null;
                              return stats ? stats.total_trips : 0;
                            },
                            cellRenderer: (params) => (
                              <Typography fontWeight="bold" className="trp-stat-value" style={{ textAlign: 'center' }}>
                                {params.value}
                              </Typography>
                            ),
                          },
                          {
                            headerName: 'North Trips (Current Month)',
                            field: 'north_trips_current',
                            width: 110,
                            minWidth: 80,
                            filter: 'agNumberColumnFilter',
                            valueGetter: (params) => {
                              const code = getReportingCodeFromTrip(params.data);
                              const stats = code ? driverStatsMap[code] : null;
                              return stats ? stats.north_trips : 0;
                            },
                            cellRenderer: (params) => (
                              <Typography fontWeight="bold" className="trp-stat-value" style={{ textAlign: 'center' }}>
                                {params.value}
                              </Typography>
                            ),
                          },
                          {
                            headerName: 'North % (Current Month)',
                            field: 'north_percentage_current',
                            width: 100,
                            minWidth: 80,
                            filter: 'agNumberColumnFilter',
                            valueGetter: (params) => {
                              const code = getReportingCodeFromTrip(params.data);
                              const stats = code ? driverStatsMap[code] : null;
                              return stats ? parseFloat(stats.north_trip_percentage || 0) : 0;
                            },
                            cellRenderer: (params) => {
                              const code = getReportingCodeFromTrip(params.data);
                              const stats = code ? driverStatsMap[code] : null;
                              const pct = stats ? parseFloat(stats.north_trip_percentage || 0).toFixed(1) : "-";
                              const pctNum = stats ? Number(stats.north_trip_percentage || 0) : null;
                              const pctClass = pctNum === null ? "" : pctNum < 25 ? "trp-percentage-low" : pctNum < 50 ? "trp-percentage-medium" : "trp-percentage-high";
                              return (
                                <Typography fontWeight="bold" className={`trp-stat-value ${pctClass}`} style={{ textAlign: 'center' }}>
                                  {pct === "-" ? "-" : `${pct}%`}
                                </Typography>
                              );
                            },
                          },
                          {
                            headerName: 'Actions',
                            field: 'actions',
                            width: 100,
                            filter: false,
                            sortable: false,
                            cellRenderer: (params) => (
                              <Box className="trp-actions-modern" style={{ display: 'flex', gap: '4px' }}>
                                <Tooltip title="Edit">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEdit(params.data)}
                                    className="trp-edit-btn-modern"
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDelete(params.data.id)}
                                    className="trp-delete-btn-modern"
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            ),
                          },
                        ]}
                        defaultColDef={{
                          resizable: true,
                          sortable: true,
                          filter: true,
                          floatingFilter: true,
                        }}
                        rowHeight={40}
                        headerHeight={40}
                        pagination={false}
                        suppressRowClickSelection={true}
                        enableCellTextSelection={true}
                      />
                    </div>
                  ) : (
                    <Box className="trp-empty-state-modern">
                      <Box className="trp-empty-icon-modern">📋</Box>
                      <Typography
                        variant="h6"
                        className="trp-empty-title-modern"
                      >
                        {translations["No trips found"] || "No trips found"}
                      </Typography>
                      <Typography
                        variant="body2"
                        className="trp-empty-description-modern"
                      >
                        {translations[
                          "Get started by adding your first trip registration"
                        ] ||
                          "Get started by adding your first trip registration"}
                      </Typography>
                      <button
                        className="trp-btn-primary-modern"
                        onClick={startNewTripWorkflow}
                      >
                        {translations["Add First Trip"] || "Add First Trip"}
                      </button>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === "allocation" && !isDriver && (
          <div className="trp-content-section trp-allocation-workspace">
            <div className="trp-allocation-toolbar">
              <div>
                <Typography variant="h6">Tomorrow Load Allocation</Typography>
                <Typography variant="body2" color="text.secondary">Fair-region guidance with controlled operational overrides</Typography>
              </div>
              <TextField type="date" size="small" label="Loading date" value={allocationDate}
                onChange={(e) => setAllocationDate(e.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" placeholder="Search driver, code or truck" value={allocationSearch}
                onChange={(e) => setAllocationSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />
              <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchAllocations}>Refresh</Button>
              <Button variant="contained" startIcon={<DownloadIcon />} onClick={exportAllocations} disabled={!filteredAllocations.length}>Excel</Button>
            </div>
            <div className="trp-allocation-summary">
              <span><strong>{allocationRows.length}</strong> registered</span>
              <span><strong>{allocationRows.filter(r => r.allocated_region).length}</strong> assigned</span>
              <span><strong>{allocationRows.filter(r => r.allocated_region === "NORTH").length}</strong> North</span>
              <span><strong>{allocationRows.filter(r => r.allocated_region === "SOUTH").length}</strong> South</span>
            </div>
            {allocationMessage && <Alert severity={allocationMessage.type}>{allocationMessage.text}</Alert>}
            {allocationLoading ? <Box className="trp-allocation-loading"><CircularProgress /></Box> : (
              <div className="trp-allocation-table-wrap">
                <table className="trp-allocation-table">
                  <thead><tr><th>Truck / Driver</th><th>Loading</th><th>South</th><th>North</th><th>Entitlement</th><th>Reserved North</th><th>Available Gap</th><th>Suggested</th><th>Assign Region</th><th>Override reason</th><th>Status</th><th>Action</th></tr></thead>
                  <tbody>{filteredAllocations.map((row) => {
                    const isOverride = row.allocated_region && row.allocated_region !== row.suggested_region;
                    const saveDisabled = !row.dirty || !row.allocated_region;
                    return <tr key={row.id} className={isOverride ? "trp-allocation-override" : ""}>
                      <td data-label="Truck / Driver">
                        <strong>{row.truck_id || "No truck"}</strong>
                        <small>{cleanDriverName(row.driver_id)}</small>
                      </td>
                      <td data-label="Loading">{moment(row.loading_time).format("DD/MM/YYYY HH:mm")}</td>
                      <td data-label="South">{row.south_trips}</td><td data-label="North">{row.north_trips}</td>
                      <td data-label="Entitlement">{row.north_entitlement || 0}</td>
                      <td data-label="Reserved North">{row.upcoming_north_allocations || 0}</td>
                      <td data-label="Available Gap"><strong>{row.entitlement_gap || 0}</strong></td>
                      <td data-label="Suggested"><span className={`trp-region-suggestion ${row.suggested_region.toLowerCase()}`}>{row.suggested_region}</span></td>
                      <td data-label="Assign Region"><select value={row.allocated_region || ""} onChange={(e) => updateAllocation(row.id, "allocated_region", e.target.value)}><option value="">Select</option><option value="NORTH">NORTH</option><option value="SOUTH">SOUTH</option></select></td>
                      <td data-label="Override reason"><input value={row.override_reason || ""} placeholder="Optional remark" onChange={(e) => updateAllocation(row.id, "override_reason", e.target.value)} /></td>
                      <td data-label="Status"><span className={`trp-allocation-status ${row.allocated_region ? "assigned" : "pending"}`}>{row.allocated_region ? "ASSIGNED" : "PENDING"}</span>{row.allocated_by && <small>{row.allocated_by}</small>}</td>
                      <td data-label="Action"><Button size="small" variant="contained" disabled={saveDisabled} onClick={() => saveAllocation(row)}>Save</Button></td>
                    </tr>;
                  })}</tbody>
                </table>
                {!filteredAllocations.length && <div className="trp-allocation-empty">No registered trips found for {moment(allocationDate).format("DD/MM/YYYY")}.</div>}
              </div>
            )}
          </div>
        )}

        {/* Region Distribution Tab */}
        {activeTab === "region" && !isDriver && (
          <div className="trp-content-section">
            <Box className="trp-region-wrapper">
              <Box className="trp-card-content-modern">
                {/* Header */}
                <Box className="trp-card-header-modern trp-region-header">
                  <Box className="trp-region-title-section">
                    <Typography variant="h6" className="trp-region-title">
                      Region Distribution
                    </Typography>
                    {regionStats && (
                      <Box className="trp-region-stats">
                        <Chip
                          label={`${regionStats.totalDrivers} drivers`}
                          size="small"
                          variant="outlined"
                        />
                        <Chip
                          label={`${regionStats.totalTrips} total trips`}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </Box>
                    )}
                  </Box>

                  {/* Action Buttons */}
                  <Box className="trp-action-buttons-modern">
                    <TextField
                      size="small"
                      placeholder="Search drivers..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon />
                          </InputAdornment>
                        ),
                      }}
                      className="trp-search-field"
                    />
                    <button
                      className="trp-btn-secondary-modern trp-btn-with-icon"
                      onClick={handleExportRegionData}
                      disabled={!regionData.length}
                    >
                      <DownloadIcon className="trp-btn-icon-modern" />
                      Export
                    </button>
                    <button
                      className="trp-btn-primary-modern trp-btn-with-icon"
                      onClick={() => fetchRegionData()}
                      disabled={regionLoading}
                    >
                      <RefreshIcon className="trp-btn-icon-modern" />
                      Refresh
                    </button>
                  </Box>
                </Box>

                {/* Filters Section */}
                <Box className="trp-filters-section-modern">
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} md={4}>
                      <div className="trp-form-group">
                        <div className="custom-dropdown">
                          <input
                            type="text"
                            className="trp-form-input"
                            value={
                              filterDriverCode
                                ? drivers.find(
                                    (d) =>
                                      d.reporting_driver_code ===
                                      filterDriverCode
                                  )?.driver_name || ""
                                : ""
                            }
                            onClick={() => setShowDriverDropdown(true)}
                            readOnly
                            placeholder="Click to select driver"
                          />
                          {showDriverDropdown && (
                            <div
                              className={`driver-dropdown-menu`}
                              ref={(el) => el && checkDropdownPosition(el)}
                            >
                              <div className="driver-search-box">
                                <input
                                  type="text"
                                  placeholder="Search driver..."
                                  value={driverSearchTerm}
                                  onChange={(e) =>
                                    setDriverSearchTerm(e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  className="driver-search-input"
                                  autoFocus
                                />
                              </div>
                              <div className="driver-options-container">
                                <div
                                  className={`driver-option ${
                                    !selectedDriverTemp ? "selected" : ""
                                  }`}
                                  onClick={() => {
                                    setSelectedDriverTemp(null);
                                    setFilterDriverCode("");
                                    fetchRegionData(
                                      filterMonth,
                                      filterYear,
                                      ""
                                    );
                                    setShowDriverDropdown(false);
                                  }}
                                >
                                  <div className="driver-name">All Drivers</div>
                                </div>
                                {filteredDrivers.length > 0 ? (
                                  filteredDrivers.map((driver) => (
                                    <div
                                      key={driver.reporting_driver_code}
                                      className={`driver-option ${
                                        selectedDriverTemp?.reporting_driver_code ===
                                        driver.reporting_driver_code
                                          ? "selected"
                                          : ""
                                      }`}
                                      onClick={() =>
                                        setSelectedDriverTemp(driver)
                                      }
                                    >
                                      <div className="driver-name">
                                        {driver.driver_name}
                                      </div>
                                      {driver.reporting_driver_code && (
                                        <div className="driver-code">
                                          {driver.reporting_driver_code}
                                        </div>
                                      )}
                                    </div>
                                  ))
                                ) : (
                                  <div className="no-results">
                                    No drivers found
                                  </div>
                                )}
                              </div>
                              <div className="driver-dropdown-actions">
                                <button
                                  type="button"
                                  className="driver-cancel-btn"
                                  onClick={() => {
                                    setShowDriverDropdown(false);
                                    setDriverSearchTerm("");
                                    setSelectedDriverTemp(null);
                                  }}
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  className="driver-select-btn"
                                  onClick={() => {
                                    if (selectedDriverTemp) {
                                      setFilterDriverCode(
                                        selectedDriverTemp.reporting_driver_code
                                      );
                                      fetchRegionData(
                                        filterMonth,
                                        filterYear,
                                        selectedDriverTemp.reporting_driver_code
                                      );
                                    }
                                    setShowDriverDropdown(false);
                                    setDriverSearchTerm("");
                                    setSelectedDriverTemp(null);
                                  }}
                                  disabled={!selectedDriverTemp}
                                >
                                  Select Driver
                                </button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Month"
                        value={filterMonth}
                        onChange={(e) => setFilterMonth(e.target.value)}
                      >
                        {Array.from({ length: 12 }, (_, i) => (
                          <MenuItem key={i + 1} value={i + 1}>
                            {moment().month(i).format("MMMM")}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Year"
                        value={filterYear}
                        onChange={(e) => setFilterYear(e.target.value)}
                      >
                        {Array.from({ length: 5 }, (_, i) => {
                          const year = moment().year() - 2 + i;
                          return (
                            <MenuItem key={year} value={year}>
                              {year}
                            </MenuItem>
                          );
                        })}
                      </TextField>
                    </Grid>
                    <Grid item xs={12} md={2}>
                      <Tooltip title="Refresh data">
                        <IconButton
                          aria-label="refresh"
                          size="large"
                          onClick={handleFilterChange}
                          disabled={regionLoading}
                          className="trp-refresh-icon-btn"
                        >
                          <RefreshIcon />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Box>

                {/* Data Table */}
                <Box className="trp-table-section-modern">
                  {regionLoading ? (
                    <Box className="trp-loading-state-modern">
                      <CircularProgress size={40} />
                      <Typography
                        variant="h6"
                        className="trp-loading-text-modern"
                      >
                        Loading region distribution data...
                      </Typography>
                    </Box>
                  ) : Array.isArray(filteredRegionData) &&
                    filteredRegionData.length > 0 ? (
                    <>
                      <div className="ag-theme-alpine" style={{ height: '600px', width: '100%' }}>
                        <AgGridReact
                          rowData={pagedRegionData}
                          columnDefs={[
                            {
                              headerName: 'No.',
                              field: 'serial',
                              width: 60,
                              filter: false,
                              sortable: false,
                              valueGetter: (params) => {
                                const index = params.api.getDisplayedRowAtIndex(params.node.rowIndex).rowIndex;
                                return (currentPage - 1) * perPage + index + 1;
                              },
                              cellRenderer: (params) => (
                                <Typography variant="body2" className="trp-serial-number">
                                  {params.value}
                                </Typography>
                              ),
                            },
                            {
                              headerName: 'Driver Code',
                              field: 'reporting_driver_code',
                              width: 120,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => (
                                <Typography
                                  variant="body2"
                                  fontWeight="bold"
                                  className="trp-driver-code-modern"
                                  title={params.value}
                                >
                                  {params.value || "-"}
                                </Typography>
                              ),
                            },
                            {
                              headerName: 'Driver Name',
                              field: 'driver_name',
                              width: 200,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => (
                                <Box className="trp-driver-info-modern" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <PersonIcon className="trp-driver-icon-modern" fontSize="small" />
                                  <Typography
                                    variant="body2"
                                    className="trp-driver-name-modern"
                                    title={params.value}
                                  >
                                    {params.value || "-"}
                                  </Typography>
                                </Box>
                              ),
                            },
                            {
                              headerName: 'South Trips',
                              field: 'south_trips',
                              width: 100,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => (
                                <Typography
                                  fontWeight="bold"
                                  className="trp-trip-count-modern"
                                  title={`South Trips: ${params.value || 0}`}
                                >
                                  {params.value || 0}
                                </Typography>
                              ),
                            },
                            {
                              headerName: 'North Trips',
                              field: 'north_trips',
                              width: 100,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => (
                                <Typography
                                  fontWeight="bold"
                                  className="trp-north-count-modern"
                                  title={`North Trips: ${params.value || 0}`}
                                >
                                  {params.value || 0}
                                </Typography>
                              ),
                            },
                            {
                              headerName: 'North %',
                              field: 'north_trip_percentage',
                              width: 100,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => {
                                const pct = parseFloat(params.value || 0);
                                const pctClass = pct < 25 ? "trp-percentage-low" : pct < 50 ? "trp-percentage-medium" : "trp-percentage-high";
                                return (
                                  <Typography
                                    fontWeight="bold"
                                    className={`trp-percentage-modern ${pctClass}`}
                                    title={`North Trip Percentage: ${pct.toFixed(1)}%`}
                                  >
                                    {pct.toFixed(1)}%
                                  </Typography>
                                );
                              },
                            },
                            {
                              headerName: 'Actions',
                              field: 'actions',
                              width: 80,
                              filter: false,
                              sortable: false,
                              cellRenderer: (params) => (
                                <Tooltip title="View Trip Details">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDriverClick(params.data)}
                                    className="trp-view-btn-modern"
                                  >
                                    <VisibilityIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              ),
                            },
                          ]}
                          defaultColDef={{
                            resizable: true,
                            sortable: false,
                            filter: false,
                          }}
                          rowHeight={40}
                          headerHeight={40}
                          pagination={false}
                          suppressRowClickSelection={true}
                          enableCellTextSelection={true}
                        />
                      </div>
                      {renderPagination()}
                    </>
                  ) : (
                    <Alert severity="info" className="trp-empty-alert-modern">
                      No region distribution data found for the selected
                      criteria.
                    </Alert>
                  )}
                </Box>
              </Box>
            </Box>
          </div>
        )}
      </div>
      {renderDriverDetails()}
    </div>
  );
};

export default TripRegistration;
